import React, { useEffect, useRef, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { colorForUser, initialsOf, isFamilyContact } from "../lib/colors";
import { SHAPES, COLORS, svgFor } from "../lib/mapIdentity";

/**
 * MapView (MapLibre GL / WebGL)
 * -----------------------------------------------------------------
 * Fully GL-accelerated map. Every pin, pulse, label and geofence is a
 * WebGL layer — nothing lives in the DOM. This lets 100+ markers pan
 * at 60 FPS on modest hardware.
 *
 * Public API is identical to the previous Leaflet MapView so no page
 * has to change:
 *   - me, contacts, geofences, focusUser, highlightUserId
 *   - onMapClick(latlng), onContactOpen(contact)
 *   - responds to window `recenter` custom events
 */

// -------- CARTO Voyager raster style (free, no API key) --------
const VOYAGER_STYLE = {
  version: 8,
  glyphs:
    "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
  sources: {
    "carto-voyager": {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        "https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        "https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        "https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      ],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
    },
  },
  layers: [
    {
      id: "carto-voyager-layer",
      type: "raster",
      source: "carto-voyager",
      minzoom: 0,
      maxzoom: 22,
    },
  ],
};

const MAP_SESSION_KEY = "proximity:mapView";
const MAP_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Approximate a metric circle as a lng/lat polygon (64 vertices).
function circleGeoJSON(lng, lat, radiusM, points = 64) {
  const coords = [];
  const distX = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
  const distY = radiusM / 110540;
  for (let i = 0; i <= points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    coords.push([lng + distX * Math.cos(theta), lat + distY * Math.sin(theta)]);
  }
  return { type: "Polygon", coordinates: [coords] };
}

// Build a FeatureCollection of contact points with all rendering hints.
function validLngLat(point) {
  return Number.isFinite(Number(point?.lat)) &&
    Number.isFinite(Number(point?.lng)) &&
    Math.abs(Number(point.lat)) <= 90 &&
    Math.abs(Number(point.lng)) <= 180;
}

function contactsToGeoJSON(contacts, highlightId) {
  const validContacts = contacts.filter((contact) => validLngLat(contact.latest));
  const occupied = new Map();
  validContacts.forEach((contact) => {
    const key = `${Number(contact.latest.lat).toFixed(5)}:${Number(contact.latest.lng).toFixed(5)}`;
    const group = occupied.get(key) || [];
    group.push(contact);
    occupied.set(key, group);
  });

  return {
    type: "FeatureCollection",
    features: validContacts.map((c) => {
        const lat = Number(c.latest.lat);
        const lng = Number(c.latest.lng);
        const key = `${lat.toFixed(5)}:${lng.toFixed(5)}`;
        const group = occupied.get(key);
        const duplicateIndex = group.indexOf(c);
        const duplicateCount = group.length;
        // Nearby/identical fixes are common for families in the same home.
        // Offset only the rendered point a few metres so every person remains
        // tappable; keep the true coordinates in properties for context.
        const offsetMeters = duplicateCount > 1 ? 15 : 0;
        const angle = duplicateCount > 1 ? (Math.PI * 2 * duplicateIndex) / duplicateCount : 0;
        const displayLat = lat + (offsetMeters * Math.sin(angle)) / 110540;
        const displayLng = lng + (offsetMeters * Math.cos(angle)) / (111320 * Math.cos((lat * Math.PI) / 180));
        const isFam = isFamilyContact(c);
        const shape = c.map_shape || (isFam ? "heart" : "circle");
        const colorKey = c.map_color || (isFam ? "red" : "royalblue");
        return {
          type: "Feature",
          id: c.user_id,
          properties: {
            user_id: c.user_id,
            name: c.name || "?",
            display_name: c.display_name || c.name || "?",
            initials: initialsOf(c.name),
            color: isFam ? "#FF2D55" : colorForUser(c.user_id),
            map_shape: shape,
            map_color: colorKey,
            isFamily: isFam,
            relation: c.relation || "friend",
            distance_miles: c.distance_miles ?? null,
            highlighted: c.user_id === highlightId,
            actual_lat: lat,
            actual_lng: lng,
          },
          geometry: {
            type: "Point",
            coordinates: [displayLng, displayLat],
          },
        };
      }),
  };
}

function meToGeoJSON(me) {
  if (!validLngLat(me)) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        id: "me",
        properties: { name: "You", initials: "ME" },
        geometry: { type: "Point", coordinates: [me.lng, me.lat] },
      },
    ],
  };
}

// A GeoJSON triangle is an Android/WebView-safe “Me” fallback. It remains
// visible even if a custom SVG sprite cannot be decoded by the WebView.
function meTriangleToGeoJSON(me) {
  if (!validLngLat(me)) return { type: "FeatureCollection", features: [] };
  const lat = Number(me.lat);
  const lng = Number(me.lng);
  const sizeM = 22;
  const dy = sizeM / 110540;
  const dx = sizeM / (111320 * Math.cos((lat * Math.PI) / 180));
  return {
    type: "FeatureCollection",
    features: [{
      type: "Feature",
      properties: { name: "You" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [lng, lat + dy],
          [lng - dx, lat - dy],
          [lng + dx, lat - dy],
          [lng, lat + dy],
        ]],
      },
    }],
  };
}

function geofencesToGeoJSON(geofences) {
  return {
    type: "FeatureCollection",
    features: (geofences || []).map((g) => ({
      type: "Feature",
      id: g.geofence_id,
      properties: {
        name: g.name || "Zone",
        color: g.color || "#FFCC00",
        radius_m: g.radius_m || 200,
      },
      geometry: circleGeoJSON(g.lng, g.lat, g.radius_m || 200),
    })),
  };
}

function highlightToGeoJSON(contacts, highlightId) {
  if (!highlightId) return { type: "FeatureCollection", features: [] };
  const c = contacts.find((x) => x.user_id === highlightId && x.latest);
  if (!c) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          color: isFamilyContact(c) ? "#FF2D55" : colorForUser(c.user_id),
        },
        geometry: circleGeoJSON(c.latest.lng, c.latest.lat, 130),
      },
    ],
  };
}

// -------- Component --------
export default function MapView({
  center,
  zoom = 14,
  me,
  contacts = [],
  geofences = [],
  onMapClick,
  onContactOpen,
  onRenderState,
  focusUser,
  highlightUserId,
  height = "100vh",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = React.useState(false);
  const hasInitiallyCentered = useRef(false);
  const lastFocusKey = useRef(null);
  const lastTap = useRef({});
  const popupRef = useRef(null);

  const fallback = [-98.5795, 39.8283]; // Central US

  // -------- Restore previously saved view (session-scoped) --------
  // If the user leaves the map screen and comes back, or refreshes the tab,
  // we rehydrate the last known {center, zoom} instead of resetting to a
  // global overview. Session storage is per-tab and clears when the tab
  // closes, matching intuitive "where I was just looking" behavior.
  const restored = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem(MAP_SESSION_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (
        typeof v?.lng !== "number" ||
        typeof v?.lat !== "number" ||
        typeof v?.zoom !== "number"
      ) return null;
      if (v.savedAt && Date.now() - v.savedAt > MAP_SESSION_MAX_AGE_MS) return null;
      return v;
    } catch { return null; }
  }, []);

  const initialCenter =
    restored
      ? [restored.lng, restored.lat]
      : center
        ? [center[1] ?? center.lng ?? center[0], center[0] ?? center.lat ?? center[1]]
        : me
          ? [me.lng, me.lat]
          : fallback;
  const initialZoom = restored ? restored.zoom : (me ? zoom : 4);
  const initialCenterRef = useRef(initialCenter);
  const initialZoomRef = useRef(initialZoom);
  const meLat = me?.lat;
  const meLng = me?.lng;

  // -------- Map setup (runs once) --------
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: VOYAGER_STYLE,
      center: initialCenterRef.current,
      zoom: initialZoomRef.current,
      attributionControl: { compact: true },
      pitchWithRotate: false,
      dragRotate: false,
      touchPitch: false,
    });
    mapRef.current = map;

    map.on("load", () => {
      // ---- Register shape sprites for the Map Identity System ----
      const combos = [];
      for (const s of SHAPES) for (const c of COLORS) combos.push([s, c.key]);
      combos.push(["triangle", "royalblue"]); // explicit ME icon
      combos.forEach(([shape, colorKey]) => {
        const name = `identity_${shape}_${colorKey}`;
        if (map.hasImage(name)) return;
        const img = new Image(128, 128);
        img.onload = () => {
          try {
            if (!map.hasImage(name)) map.addImage(name, img, { pixelRatio: 2 });
            map.triggerRepaint();
          } catch {}
        };
        img.src = `data:image/svg+xml;utf8,${encodeURIComponent(svgFor(shape, colorKey, 128))}`;
      });
      // Sources
      map.addSource("geofences", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addSource("highlight", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addSource("contacts", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addSource("me", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addSource("me-triangle", { type: "geojson", data: { type: "FeatureCollection", features: [] } });

      // Geofence fill + dashed outline
      map.addLayer({
        id: "geofence-fill",
        type: "fill",
        source: "geofences",
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.12 },
      });
      map.addLayer({
        id: "geofence-line",
        type: "line",
        source: "geofences",
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2,
          "line-dasharray": [3, 2],
          "line-opacity": 0.8,
        },
      });

      // Highlight ring (big transparent halo around focused user)
      map.addLayer({
        id: "highlight-ring",
        type: "fill",
        source: "highlight",
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.18 },
      });
      map.addLayer({
        id: "highlight-line",
        type: "line",
        source: "highlight",
        paint: { "line-color": ["get", "color"], "line-width": 2, "line-opacity": 0.7 },
      });

      // Contact pulse ring (larger, low-opacity)
      map.addLayer({
        id: "contact-pulse",
        type: "circle",
        source: "contacts",
        paint: {
          "circle-color": ["get", "color"],
          "circle-opacity": 0.28,
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            8, 12, 14, 22, 18, 30,
          ],
        },
      });

      // Always render a solid, clickable fallback underneath the custom SVG.
      // Sprite decoding/loading can fail on some WebViews; a contact must
      // never disappear merely because its optional identity image did.
      map.addLayer({
        id: "contact-fallback-pin",
        type: "circle",
        source: "contacts",
        paint: {
          "circle-color": ["get", "color"],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 6, 14, 10, 18, 14],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });

      // Contact pin — custom shape+color icon per contact (Map Identity System)
      map.addLayer({
        id: "contact-pin",
        type: "symbol",
        source: "contacts",
        layout: {
          "icon-image": ["concat", "identity_", ["get", "map_shape"], "_", ["get", "map_color"]],
          "icon-size": [
            "interpolate", ["linear"], ["zoom"],
            8, 0.18, 14, 0.34, 18, 0.5,
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });

      // Contact label — display_name sits BELOW the shape icon
      map.addLayer({
        id: "contact-label",
        type: "symbol",
        source: "contacts",
        layout: {
          "text-field": ["get", "display_name"],
          "text-font": ["Open Sans Semibold"],
          "text-size": [
            "interpolate", ["linear"], ["zoom"],
            8, 9, 14, 11, 18, 13,
          ],
          "text-offset": [0, 1.4],
          "text-anchor": "top",
          "text-allow-overlap": false,
          "text-ignore-placement": false,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(0,0,0,0.55)",
          "text-halo-width": 1.4,
        },
      });

      // Me pulse + pin + label
      map.addLayer({
        id: "me-pulse",
        type: "circle",
        source: "me",
        paint: {
          "circle-color": "#007AFF",
          "circle-opacity": 0.3,
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            8, 14, 14, 26, 18, 36,
          ],
        },
      });
      map.addLayer({
        id: "me-fallback-pin",
        type: "circle",
        source: "me",
        paint: {
          "circle-color": "#007AFF",
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 7, 14, 11, 18, 15],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
      map.addLayer({
        id: "me-triangle-fallback",
        type: "fill",
        source: "me-triangle",
        paint: { "fill-color": "#007AFF", "fill-opacity": 1 },
      });
      map.addLayer({
        id: "me-triangle-fallback-outline",
        type: "line",
        source: "me-triangle",
        paint: { "line-color": "#ffffff", "line-width": 2 },
      });
      map.addLayer({
        id: "me-pin",
        type: "symbol",
        source: "me",
        layout: {
          "icon-image": "identity_triangle_royalblue",
          "icon-size": [
            "interpolate", ["linear"], ["zoom"],
            8, 0.22, 14, 0.42, 18, 0.6,
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });
      map.addLayer({
        id: "me-label",
        type: "symbol",
        source: "me",
        layout: {
          "text-field": "ME",
          "text-font": ["Open Sans Semibold"],
          "text-size": [
            "interpolate", ["linear"], ["zoom"],
            8, 9, 14, 11, 18, 13,
          ],
          "text-offset": [0, 1.6],
          "text-anchor": "top",
          "text-allow-overlap": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(0,0,0,0.55)",
          "text-halo-width": 1.4,
        },
      });

      // -------- Interaction --------

      // Pointer cursor over contacts
      ["contact-pin", "contact-fallback-pin"].forEach((layer) => {
        map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
      });

      // Contact click — single tap shows popup, double-tap opens sheet
      const onContactClick = (e) => {
        const f = e.features?.[0];
        if (!f) return;
        e.originalEvent?.stopPropagation?.();
        const id = f.properties.user_id;
        const now = Date.now();
        const prev = lastTap.current[id] || 0;
        lastTap.current[id] = now;
        if (now - prev < 400 && onContactOpenRef.current) {
          const c = contactsRef.current.find((x) => x.user_id === id);
          if (c) onContactOpenRef.current(c);
          return;
        }
        // Single tap — show popup
        const props = f.properties;
        const dist = props.distance_miles;
        const distStr =
          dist == null || dist === "null"
            ? ""
            : Number(dist) < 0.19
              ? `${Math.round(Number(dist) * 5280)} ft`
              : `${Number(dist).toFixed(2)} mi`;
        const html = `
          <div style="font-family:Chivo,-apple-system,sans-serif;color:#111">
            <div style="font-weight:900;letter-spacing:-0.02em;font-size:15px">${escapeHtml(props.name)}</div>
            <div style="font-size:11px;color:#666;text-transform:capitalize;margin-top:2px">${escapeHtml(props.relation)}</div>
            ${distStr ? `<div style="font-family:ui-monospace,SF Mono,monospace;font-size:11px;margin-top:4px">${distStr}</div>` : ""}
            <div style="font-size:10px;color:#999;margin-top:6px">Double-tap to manage</div>
          </div>`;
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({ closeButton: false, offset: 18, maxWidth: "220px" })
          .setLngLat(f.geometry.coordinates)
          .setHTML(html)
          .addTo(map);
      };
      map.on("click", "contact-pin", onContactClick);
      map.on("click", "contact-fallback-pin", onContactClick);
      map.on("click", "contact-label", onContactClick);

      // Empty-map click callback + dismiss popup
      map.on("click", (e) => {
        // Skip if a contact layer already handled this event
        const hits = map.queryRenderedFeatures(e.point, {
          layers: ["contact-pin", "contact-fallback-pin", "contact-label"],
        });
        if (hits.length > 0) return;
        popupRef.current?.remove();
        if (onMapClickRef.current) {
          onMapClickRef.current({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        }
      });

      // Only mark as ready after all layers exist. Using React state so the
      // sync effect below re-runs when this flips true (avoids a race where
      // `me` populates before `load` fires).
      setReady(true);
    });

    // Zoom controls (top-right, pretty)
    map.addControl(new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }), "top-right");

    return () => {
      // Persist final view before teardown so the next mount / tab restores here.
      try {
        const c = map.getCenter();
        window.sessionStorage.setItem(
          MAP_SESSION_KEY,
          JSON.stringify({ lng: c.lng, lat: c.lat, zoom: map.getZoom(), savedAt: Date.now() })
        );
      } catch { /* storage disabled — non-fatal */ }
      setReady(false);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // -------- Persist view continuously (throttled) + on tab hide --------
  // The unmount handler above catches SPA navigation. This handler catches
  // tab-close / phone-lock / page-refresh where React doesn't get an unmount.
  useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    if (!map) return;

    const persist = () => {
      try {
        const c = map.getCenter();
        window.sessionStorage.setItem(
          MAP_SESSION_KEY,
          JSON.stringify({ lng: c.lng, lat: c.lat, zoom: map.getZoom(), savedAt: Date.now() })
        );
      } catch { /* ignore */ }
    };

    // Throttled save on user pan/zoom — MapLibre fires moveend once per gesture.
    let pending = null;
    const onMoveEnd = () => {
      if (pending) return;
      pending = setTimeout(() => { pending = null; persist(); }, 200);
    };
    map.on("moveend", onMoveEnd);

    const onVisibility = () => { if (document.visibilityState === "hidden") persist(); };
    const onPageHide = () => persist();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      map.off("moveend", onMoveEnd);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      if (pending) clearTimeout(pending);
    };
  }, [ready]);

  // -------- Refs to props that change frequently (accessed inside listeners) --------
  const contactsRef = useRef(contacts);
  const onContactOpenRef = useRef(onContactOpen);
  const onMapClickRef = useRef(onMapClick);
  const onRenderStateRef = useRef(onRenderState);
  useEffect(() => { contactsRef.current = contacts; }, [contacts]);
  useEffect(() => { onContactOpenRef.current = onContactOpen; }, [onContactOpen]);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);
  useEffect(() => { onRenderStateRef.current = onRenderState; }, [onRenderState]);

  // -------- Data syncing (fires whenever the props change OR when ready flips) --------
  const syncSources = useCallback(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getSource("me")?.setData(meToGeoJSON(me));
    map.getSource("me-triangle")?.setData(meTriangleToGeoJSON(me));
    map.getSource("contacts")?.setData(contactsToGeoJSON(contacts, highlightUserId));
    map.getSource("geofences")?.setData(geofencesToGeoJSON(geofences));
    map.getSource("highlight")?.setData(highlightToGeoJSON(contacts, highlightUserId));
    onRenderStateRef.current?.({
      ready: true,
      meMarker: validLngLat(me),
      contactsWithCoordinates: contacts.filter((contact) => validLngLat(contact.latest)).length,
      fallbackMarkers: true,
    });
  }, [ready, me, contacts, geofences, highlightUserId]);

  useEffect(() => {
    syncSources();
  }, [syncSources]);

  // -------- Initial centering (only ONCE per mount, on first fix) --------
  // Skipped entirely when we've rehydrated a saved view — the user was just
  // looking at that spot; snapping them to their current GPS position would
  // undo the restore.
  useEffect(() => {
    if (restored) { hasInitiallyCentered.current = true; return; }
    if (meLat == null || meLng == null) return;
    const map = mapRef.current;
    if (!map) return;
    if (hasInitiallyCentered.current) return;
    map.jumpTo({ center: [meLng, meLat], zoom: Math.max(map.getZoom(), 14) });
    hasInitiallyCentered.current = true;
  }, [meLat, meLng, restored]);

  // -------- External recenter (window event OR "My Location" button) --------
  useEffect(() => {
    const onRecenter = (e) => {
      const map = mapRef.current;
      if (!map) return;
      const target = e?.detail || { lat: meLat, lng: meLng };
      if (!target || target.lat == null) return;
      map.flyTo({ center: [target.lng, target.lat], zoom: Math.max(map.getZoom(), 15), duration: 600 });
    };
    window.addEventListener("recenter", onRecenter);
    return () => window.removeEventListener("recenter", onRecenter);
  }, [meLat, meLng]);

  // -------- Focus (contact-name click deep-link) --------
  useEffect(() => {
    if (!focusUser || focusUser.lat == null) return;
    if (lastFocusKey.current === focusUser.key) return;
    lastFocusKey.current = focusUser.key;
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({ center: [focusUser.lng, focusUser.lat], zoom: Math.max(map.getZoom(), 15), duration: 800 });
  }, [focusUser]);

  return (
    <div
      ref={containerRef}
      data-testid="mapview-webgl"
      style={{ height, width: "100%", background: "#0a0a0a" }}
    />
  );
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
